/**
 * Two-step spoken confirmation for destructive voice actions.
 *
 * A handler first calls `prompt(key, question)` and returns its result, which
 * performs nothing and asks the user the question. The model is told to pass
 * `confirm: true` only after the user says yes. `isConfirmed(key, args)` then
 * honours that flag only when a prompt for the same key was issued recently,
 * so a stray `confirm: true` from the model never deletes anything on its own.
 */
import type {VoiceActionResult} from './VoiceControlSurfaceContext';

interface PendingConfirmation {
    key: string;
    issuedAt: number;
}

export interface VoiceConfirmationGate {
    prompt: (key: string, question: string) => VoiceActionResult;
    isConfirmed: (key: string, args: Record<string, unknown>) => boolean;
    clear: () => void;
}

export const createVoiceConfirmationGate = (
    ttlMs = 120_000
): VoiceConfirmationGate => {
    let pending: PendingConfirmation | null = null;

    return {
        prompt: (key, question) => {
            pending = {key, issuedAt: Date.now()};
            return {
                status: 'ready',
                message: question,
                speakText: question,
                data: {requiresConfirmation: true, confirmationKey: key}
            };
        },
        isConfirmed: (key, args) => {
            if (args.confirm !== true || !pending) {
                return false;
            }
            const fresh =
                pending.key === key && Date.now() - pending.issuedAt <= ttlMs;
            if (fresh) {
                pending = null;
            }
            return fresh;
        },
        clear: () => {
            pending = null;
        }
    };
};

export const confirmParameter = {
    type: 'boolean',
    description:
        'Pass true only after the user has explicitly said yes to the confirmation question this tool asked. Never pass true on the first call.'
} as const;
